import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ROLES } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Warning, Database, Trash, PencilSimple, Key, Copy, Gear, Envelope, X as XIcon, Download, PaperPlaneTilt, ClockCounterClockwise, CheckCircle, Warning as WarningIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", role: "department", department: "" };
const EMPTY_EDIT = { name: "", role: "department", department: "" };

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resetLinkTarget, setResetLinkTarget] = useState(null);
  const [resetLinkResult, setResetLinkResult] = useState(null);
  const [sendingLink, setSendingLink] = useState(false);

  async function submitReset() {
    if (resetConfirm !== "RESET") return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-database", { confirm: "RESET" });
      toast.success(`Database reset. Cleared: ${Object.values(data.cleared || {}).reduce((a, b) => a + b, 0)} records.`);
      setResetOpen(false);
      setResetConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setResetting(false);
    }
  }

  const [settings, setSettings] = useState({ yearly_prefix: false });
  const [savingSettings, setSavingSettings] = useState(false);

  // Backup settings state
  const [backup, setBackup] = useState({ recipients: [], updated_at: null, updated_by: null });
  const [newRecipient, setNewRecipient] = useState("");
  const [savingBackup, setSavingBackup] = useState(false);
  const [sendingBackup, setSendingBackup] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [backupLogs, setBackupLogs] = useState([]);
  const [backupRange, setBackupRange] = useState({ start: "", end: "" });

  async function loadBackupLogs() {
    try {
      const { data } = await api.get("/backup/logs", { params: { limit: 20 } });
      setBackupLogs(data || []);
    } catch { setBackupLogs([]); }
  }

  async function saveBackupRecipients() {
    setSavingBackup(true);
    try {
      const { data } = await api.put("/settings/backup", { recipients: backup.recipients });
      setBackup((b) => ({ ...b, recipients: data.recipients }));
      toast.success("Backup recipients saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save recipients");
    } finally { setSavingBackup(false); }
  }

  function addRecipient() {
    const e = (newRecipient || "").trim().toLowerCase();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error("Enter a valid email");
      return;
    }
    if (backup.recipients.includes(e)) { toast.message("Already added"); return; }
    setBackup((b) => ({ ...b, recipients: [...b.recipients, e] }));
    setNewRecipient("");
  }

  function removeRecipient(email) {
    setBackup((b) => ({ ...b, recipients: b.recipients.filter((r) => r !== email) }));
  }

  async function triggerBackupSendNow() {
    setSendingBackup(true);
    try {
      const params = {};
      if (backupRange.start) params.start_date = backupRange.start;
      if (backupRange.end) params.end_date = backupRange.end;
      const { data } = await api.post("/backup/send-now", null, { params });
      toast.success(`Backup send queued for ${data.recipients.join(", ")}`);
      setTimeout(loadBackupLogs, 4000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed");
    } finally { setSendingBackup(false); }
  }

  async function downloadBackupXlsx() {
    setDownloadingBackup(true);
    try {
      const token = localStorage.getItem("access_token") || "";
      const qs = new URLSearchParams();
      if (backupRange.start) qs.set("start_date", backupRange.start);
      if (backupRange.end) qs.set("end_date", backupRange.end);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/backup/xlsx${qs.toString() ? `?${qs}` : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const nm = /filename="?([^"]+)"?/.exec(cd)?.[1] || "casedist_backup.xlsx";
      const bUrl = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = bUrl; a.download = nm;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(bUrl);
      toast.success(`Downloaded ${nm}`);
      loadBackupLogs();
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally { setDownloadingBackup(false); }
  }

  async function load() {
    const [uRes, sRes, bRes] = await Promise.all([
      api.get("/auth/users"),
      api.get("/admin/settings").catch(() => ({ data: { yearly_prefix: false } })),
      api.get("/settings/backup").catch(() => ({ data: { recipients: [], updated_at: null, updated_by: null } })),
    ]);
    setUsers(uRes.data);
    setSettings(sRes.data);
    setBackup(bRes.data);
    loadBackupLogs();
  }
  useEffect(() => { load(); }, []);

  async function toggleYearlyPrefix(next) {
    setSavingSettings(true);
    try {
      const { data } = await api.patch("/admin/settings", { yearly_prefix: next });
      setSettings(data);
      toast.success(
        next
          ? "Reference format is now PREFIX-YYMM-#### (e.g. CV-2608-0001)"
          : "Reference format is now PREFIX-MM-#### (e.g. CV-08-0001)"
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save setting");
    } finally {
      setSavingSettings(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/auth/register", form);
      toast.success("User created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  }

  async function confirmDelete() {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/auth/users/${delTarget.id}`);
      toast.success(`Deleted ${delTarget.name}`);
      setDelTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  function openEdit(u) {
    setEditTarget(u);
    setEditForm({ name: u.name || "", role: u.role || "department", department: u.department || "" });
  }

  async function submitEdit(e) {
    e.preventDefault();
    if (!editTarget) return;
    setSaving(true);
    try {
      await api.patch(`/auth/users/${editTarget.id}`, editForm);
      toast.success("User updated");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  }

  async function sendResetLink() {
    if (!resetLinkTarget) return;
    setSendingLink(true);
    try {
      const { data } = await api.post(`/auth/users/${resetLinkTarget.id}/reset-link`);
      setResetLinkResult(data);
      if (data.email_sent) toast.success(`Reset link emailed to ${resetLinkTarget.email}`);
      else toast.message("No email service configured — link shown below to copy manually.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed");
    } finally { setSendingLink(false); }
  }

  function copyLink() {
    if (!resetLinkResult?.reset_link) return;
    navigator.clipboard?.writeText(resetLinkResult.reset_link);
    toast.success("Link copied to clipboard");
  }

  const isProtected = (u) => u.email === "boaccntg@gmail.com" || u.id === currentUser?.id;

  const badgeCls = {
    admin: "bg-foreground text-background",
    finance: "bg-blue-50 text-blue-800 border-blue-200",
    procurement: "bg-emerald-50 text-emerald-800 border-emerald-200",
    department: "bg-amber-50 text-amber-800 border-amber-200",
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Admin</div>
          <h1 className="section-title text-3xl sm:text-4xl">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage system users and roles.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => { setResetConfirm(""); setResetOpen(true); }} data-testid="reset-db-btn">
            <Database size={16} className="mr-2" /> Reset Database
          </Button>
          <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-user-btn"><UserPlus size={16} className="mr-2" /> New User</Button>
        </div>
      </div>

      {/* System Settings */}
      <div className="border border-border rounded-md bg-card p-5 flex items-center justify-between gap-6" data-testid="system-settings-card">
        <div className="flex items-start gap-3">
          <div className="mt-0.5"><Gear size={18} className="text-muted-foreground" /></div>
          <div>
            <div className="font-heading font-bold tracking-tight">Reference Format</div>
            <p className="text-sm text-muted-foreground mt-1">
              Include the 2-digit year in every new reference number for long-term archival clarity.
            </p>
            <div className="text-xs mt-2 tabular-nums">
              <span className="text-muted-foreground">Preview: </span>
              <span className="font-mono font-bold">
                {settings.yearly_prefix ? "CV-2608-0001" : "CV-08-0001"}
              </span>
              <span className="text-muted-foreground ml-2">
                ({settings.yearly_prefix ? "with year" : "month only"})
              </span>
            </div>
          </div>
        </div>
        <Button
          variant={settings.yearly_prefix ? "default" : "outline"}
          className={settings.yearly_prefix ? "" : "border-border"}
          disabled={savingSettings}
          onClick={() => toggleYearlyPrefix(!settings.yearly_prefix)}
          data-testid="toggle-yearly-prefix-btn"
        >
          {savingSettings ? "Saving…" : settings.yearly_prefix ? "Turn off year prefix" : "Turn on year prefix"}
        </Button>
      </div>

      {/* Weekly Backup Settings */}
      <div className="border border-border rounded-md bg-card p-5 space-y-4" data-testid="backup-settings-card">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-3">
            <div className="mt-0.5"><Envelope size={18} className="text-muted-foreground" /></div>
            <div>
              <div className="font-heading font-bold tracking-tight">Weekly Backup Recipients</div>
              <p className="text-sm text-muted-foreground mt-1">
                An Excel workbook of Requests, Quotations, Billing, Receivables and Permits is emailed every <b>Monday at 09:00 Manila</b>. Add or remove emails below.
              </p>
              {backup.updated_by && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  Last updated by {backup.updated_by} · {(backup.updated_at || "").slice(0, 10)}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              className="border-border"
              disabled={downloadingBackup}
              onClick={downloadBackupXlsx}
              data-testid="download-backup-btn"
            >
              <Download size={14} className="mr-2" />
              {downloadingBackup ? "Preparing…" : "Download now"}
            </Button>
            <Button
              disabled={sendingBackup || (backup.recipients || []).length === 0}
              onClick={triggerBackupSendNow}
              data-testid="send-backup-btn"
            >
              <PaperPlaneTilt size={14} className="mr-2" />
              {sendingBackup ? "Sending…" : "Send backup now"}
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="backup-recipients-list">
          {(backup.recipients || []).length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No recipients yet — add one below.</div>
          ) : backup.recipients.map((r) => (
            <span key={r} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-border bg-muted/40" data-testid={`backup-recipient-${r}`}>
              {r}
              <button type="button" onClick={() => removeRecipient(r)} className="text-muted-foreground hover:text-foreground" title="Remove" data-testid={`remove-recipient-${r}`}>
                <XIcon size={12} />
              </button>
            </span>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Input
            type="email"
            placeholder="finance@casedist.ph"
            value={newRecipient}
            onChange={(e) => setNewRecipient(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
            className="border-border max-w-sm"
            data-testid="new-recipient-input"
          />
          <Button type="button" variant="outline" className="border-border" onClick={addRecipient} data-testid="add-recipient-btn">
            Add
          </Button>
          <div className="flex-1" />
          <Button type="button" disabled={savingBackup} onClick={saveBackupRecipients} data-testid="save-backup-btn">
            {savingBackup ? "Saving…" : "Save recipients"}
          </Button>
        </div>

        {/* Date range filter for manual send / download */}
        <div className="border-t border-border pt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="backup-range-row">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Start date (optional)</Label>
            <Input type="date" value={backupRange.start} onChange={(e) => setBackupRange((r) => ({ ...r, start: e.target.value }))} className="border-border" data-testid="backup-start-date" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">End date (optional)</Label>
            <Input type="date" value={backupRange.end} onChange={(e) => setBackupRange((r) => ({ ...r, end: e.target.value }))} className="border-border" data-testid="backup-end-date" />
          </div>
          <div className="flex items-end gap-2">
            <Button type="button" variant="ghost" className="border border-border" onClick={() => setBackupRange({ start: "", end: "" })} data-testid="clear-backup-range-btn">
              Clear range
            </Button>
            <div className="text-[11px] text-muted-foreground pb-2">
              {backupRange.start || backupRange.end
                ? `Filtered: ${backupRange.start || "start"} → ${backupRange.end || "end"}`
                : "No filter — full history"}
            </div>
          </div>
        </div>

        {/* Send history */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center gap-2 mb-2">
            <ClockCounterClockwise size={14} className="text-muted-foreground" />
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Recent send history</div>
          </div>
          {backupLogs.length === 0 ? (
            <div className="text-xs text-muted-foreground italic py-2">No sends recorded yet.</div>
          ) : (
            <div className="border border-border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[160px]">When (UTC)</TableHead>
                    <TableHead className="w-[90px]">Trigger</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Counts</TableHead>
                    <TableHead className="w-[110px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backupLogs.map((l) => (
                    <TableRow key={l.id} data-testid={`backup-log-${l.id}`}>
                      <TableCell className="text-xs font-mono">{(l.created_at || "").slice(0, 19).replace("T", " ")}</TableCell>
                      <TableCell className="text-xs uppercase tracking-wider">{l.trigger}</TableCell>
                      <TableCell className="text-xs">{(l.recipients || []).join(", ") || "—"}</TableCell>
                      <TableCell className="text-xs">
                        {Object.entries(l.counts || {}).map(([k, v]) => (
                          <span key={k} className="mr-2"><span className="text-muted-foreground">{k}:</span> <b>{v}</b></span>
                        ))}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${
                          l.status === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                          : l.status === "skipped" ? "border-amber-200 bg-amber-50 text-amber-800"
                          : "border-rose-200 bg-rose-50 text-rose-800"
                        }`}>
                          {l.status === "success" ? <CheckCircle size={10} weight="fill" /> : <WarningIcon size={10} weight="fill" />}
                          {l.status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table className="min-w-[900px]">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[80px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${badgeCls[u.role] || ""}`}>{ROLES[u.role] || u.role}</span>
                </TableCell>
                <TableCell className="text-sm">{u.department || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{(u.created_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 hover:bg-muted"
                      title="Edit user"
                      onClick={() => openEdit(u)}
                      data-testid={`edit-user-btn-${u.id}`}
                    >
                      <PencilSimple size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                      title="Send password reset link"
                      onClick={() => { setResetLinkResult(null); setResetLinkTarget(u); }}
                      data-testid={`reset-link-btn-${u.id}`}
                    >
                      <Key size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-rose-700 hover:bg-rose-50 hover:text-rose-800 disabled:opacity-30"
                      disabled={isProtected(u)}
                      title={isProtected(u) ? (u.id === currentUser?.id ? "You cannot delete your own account" : "Primary admin cannot be deleted") : "Delete user"}
                      onClick={() => setDelTarget(u)}
                      data-testid={`delete-user-btn-${u.id}`}
                    >
                      <Trash size={16} weight="bold" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading font-black">Create User</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-border" data-testid="user-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-border" data-testid="user-email-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label><Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="border-border" data-testid="user-password-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="border-border" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="user-submit-btn">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Database dialog */}
      <Dialog open={resetOpen} onOpenChange={(v) => { setResetOpen(v); if (!v) setResetConfirm(""); }}>
        <DialogContent data-testid="reset-db-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black flex items-center gap-2 text-rose-700">
              <Warning size={20} weight="fill" /> Reset all operational data
            </DialogTitle>
            <DialogDescription>
              This will permanently delete every budget request, quotation, purchase order, billing request, invoice, client, supplier, permit, uploaded file and auto-number counter. User accounts are kept so you can still sign in. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="border border-rose-200 bg-rose-50 rounded-md p-3 text-xs text-rose-800">
              Everything you see across the modules — including the Archive — will be permanently removed. User accounts stay.
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Type <span className="font-mono font-bold text-rose-700">RESET</span> to confirm</Label>
              <Input
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                placeholder="RESET"
                className="border-border font-mono"
                data-testid="reset-confirm-input"
                autoComplete="off"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button
              onClick={submitReset}
              disabled={resetConfirm !== "RESET" || resetting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="reset-confirm-btn"
            >
              {resetting ? "Resetting…" : "Reset database"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete user dialog */}
      <Dialog open={!!delTarget} onOpenChange={(v) => { if (!v) setDelTarget(null); }}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black flex items-center gap-2 text-rose-700">
              <Warning size={20} weight="fill" /> Delete user
            </DialogTitle>
            <DialogDescription>
              This permanently removes <span className="font-semibold text-foreground">{delTarget?.name}</span> ({delTarget?.email}) from the system. Records they created remain intact. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="delete-user-confirm-btn"
            >
              {deleting ? "Deleting…" : "Delete user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black flex items-center gap-2">
              <PencilSimple size={20} weight="fill" /> Edit user
            </DialogTitle>
            <DialogDescription>
              Update name, role or department for <span className="font-semibold text-foreground">{editTarget?.email}</span>.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
              <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="border-border" data-testid="edit-user-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })} disabled={editTarget?.email === "boaccntg@gmail.com"}>
                  <SelectTrigger data-testid="edit-user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
                {editTarget?.email === "boaccntg@gmail.com" && (
                  <div className="text-[10px] text-muted-foreground mt-1">Primary admin role is locked.</div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Department</Label>
                <Input value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} className="border-border" data-testid="edit-user-department-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEditTarget(null)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="edit-user-save-btn">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password reset link dialog */}
      <Dialog open={!!resetLinkTarget} onOpenChange={(v) => { if (!v) { setResetLinkTarget(null); setResetLinkResult(null); } }}>
        <DialogContent data-testid="reset-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black flex items-center gap-2 text-amber-700">
              <Key size={20} weight="fill" /> Send password reset link
            </DialogTitle>
            <DialogDescription>
              A one-time reset link (valid for 60 minutes) will be generated for <span className="font-semibold text-foreground">{resetLinkTarget?.name}</span> ({resetLinkTarget?.email}). If email is configured it will be delivered automatically; otherwise you can copy the link and share it manually.
            </DialogDescription>
          </DialogHeader>

          {resetLinkResult && (
            <div className="space-y-2 py-2" data-testid="reset-link-result">
              <div className={`text-xs px-3 py-2 rounded border ${resetLinkResult.email_sent ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                {resetLinkResult.email_sent
                  ? `Email sent to ${resetLinkTarget?.email}. Link expires in ${resetLinkResult.expires_in_minutes} min.`
                  : `No email service configured. Copy the link below and share it. Expires in ${resetLinkResult.expires_in_minutes} min.`}
              </div>
              <div className="flex items-center gap-2">
                <Input readOnly value={resetLinkResult.reset_link} className="border-border font-mono text-xs" data-testid="reset-link-value" />
                <Button type="button" variant="outline" className="border-border" onClick={copyLink} data-testid="copy-reset-link-btn">
                  <Copy size={14} className="mr-2" /> Copy
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setResetLinkTarget(null); setResetLinkResult(null); }}>
              {resetLinkResult ? "Close" : "Cancel"}
            </Button>
            {!resetLinkResult && (
              <Button onClick={sendResetLink} disabled={sendingLink} className="bg-amber-600 hover:bg-amber-700" data-testid="send-reset-link-btn">
                {sendingLink ? "Generating…" : "Generate & send link"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
